import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { performGameAction } from '../../services/gameActionService';
import { PDFService } from '../../services/pdfService';
import './Gameplay.css';
import { useAudio } from '../../context/AudioContext';
import { FaMobileAlt } from 'react-icons/fa';

// Import types
import { 
  GameState, 
  Position, 
  GuidanceFormat, 
  Message, 
  GameActionResponse, 
  GameOverMessage,
  MessageContent
} from './types';

// Import components
import GameStatus from './components/GameStatus';
import ChatWindow from './components/ChatWindow';
import ActionInput from './components/ActionInput';
import GameOverModal from './components/GameOverModal';

const Gameplay: React.FC = () => {
  const location = useLocation();
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { playBackgroundMusic, stopBackgroundMusic, gameSoundVolume, bgMusicVolume, setBgMusicVolume, setGameSoundVolume } = useAudio();
  const [isLoading, setIsLoading] = useState(true);
  
  const [gameData, setGameData] = useState<GameState | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [totalScore, setTotalScore] = useState(0);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [failedAction, setFailedAction] = useState<string | null>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [reactionType, setReactionType] = useState<string | undefined>(undefined);
  
  // State for game over
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameOverMessage, setGameOverMessage] = useState<GameOverMessage | null>(null);
  
  // State for draggable components
  const [statsPosition, setStatsPosition] = useState<Position>({ x: 20, y: 20 });
  
  // Refs for drag functionality
  const isDraggingStats = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  
  // Store previous theme
  const [previousTheme, setPreviousTheme] = useState(document.documentElement.getAttribute('data-theme') || 'light');

  const [isStatsVisible, setStatsVisible] = useState(true);
  const [isReactionReady] = useState(false);

  // Define guidance formats
  const guidanceFormats: { [key: string]: GuidanceFormat } = {
    guide: {
      prefix: "Guide them to",
      description: "Direct action guidance"
    },
    whisper: {
      prefix: "Whisper a warning about",
      description: "Warning about dangers"
    },
    insight: {
      prefix: "Share your spiritual insight about",
      description: "Share deeper understanding"
    },
    feeling: {
      prefix: "Send them a feeling of",
      description: "Emotional guidance"
    }
  };

  // Initialize game data and start background music
  useEffect(() => {
    const state = location.state as GameState;
    
    // Check if we're missing critical game data
    if (!state || !gameId) {
      navigate('/', { replace: true });
      return;
    }

    // Validate required fields in state
    if (!state.sessionId || !state.gameType || !state.initialScenario) {
      console.error('Invalid game state:', state);
      navigate('/', { replace: true });
      return;
    }

    try {
      setGameData(state);
      
      // Start playing background music for this game type
      playBackgroundMusic(state.gameType);

      // Initialize messages array with proper typing
      const initialMessages: Message[] = [];

      // Function to read messages after they're set
      const readInitialMessages = async () => {
        try {
          // If there's history, read the last feedback and prompt
          if (state.history && state.history.length > 0) {
            const lastHistoryItem = state.history[state.history.length - 1];
            if (lastHistoryItem.response.feedback) {
              await speakMessage(lastHistoryItem.response.feedback);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            if (lastHistoryItem.response.next_prompt) {
              await speakMessage(lastHistoryItem.response.next_prompt);
            }
          } else {
            // Read the initial scenario
            await speakMessage(state.initialScenario);
          }
        } catch (error) {
          console.error('Error reading initial messages:', error);
        }
      };

      // If there's history, add it to messages
      if (state.history && state.history.length > 0) {
        state.history.forEach((historyItem) => {
          // Add user's action
          initialMessages.push({
            type: 'user',
            content: historyItem.action
          });
          
          // Add system's feedback if it exists
          if (historyItem.response.feedback) {
            initialMessages.push({
              type: 'system',
              content: historyItem.response.feedback,
              isFeedback: true
            });
          }
          
          // Add system's next prompt
          if (historyItem.response.next_prompt) {
            initialMessages.push({
              type: 'system',
              content: historyItem.response.next_prompt
            });
          }
        });
      } else {
        // Only add the initial scenario if there's no history
        initialMessages.push({ 
          type: 'system', 
          content: state.initialScenario 
        });
      }
      
      // Set messages first
      setMessages(initialMessages);
      setSuggestions((state.initialSuggestions || []).slice(0, 3));
      setTotalScore(state.initialScore || 0);
      
      // Wait a short moment for the UI to update before starting speech
      setTimeout(() => {
        readInitialMessages();
      }, 500);
      
    } catch (error) {
      console.error('Error initializing game:', error);
      navigate('/', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, []);  // Empty dependency array to run only once
  
  // Dedicated effect for handling cleanup when unmounting
  useEffect(() => {
    // This will be called when the component unmounts
    return () => {
      console.log('Gameplay component unmounting - stopping all audio');
      stopAllAudio();
    };
  }, []);

  // Drag functionality
  const handleDragStart = (
    e: React.MouseEvent,
    isDragging: React.MutableRefObject<boolean>,
    currentPosition: Position
  ) => {
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y
    };
  };

  const handleDragEnd = (isDragging: React.MutableRefObject<boolean>) => {
    isDragging.current = false;
  };

  // Mouse move handler for dragging
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingStats.current) {
        setStatsPosition({
          x: e.clientX - dragOffset.current.x,
          y: e.clientY - dragOffset.current.y
        });
      }
    };

    const handleMouseUp = () => {
      isDraggingStats.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Update the generateTranscriptPDF function to use the new service
  const generateTranscriptPDF = async () => {
    try {
      if (!gameData) {
        throw new Error('No game data available');
      }

      // Format the game name properly
      const gameName = gameData.gameType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

      const transcriptData = {
        gameType: gameData.gameType,
        gameName,
        difficulty: gameData.difficulty,
        finalScore: totalScore,
        trustLevel: calculateTrustLevel(totalScore).level,
        messages
      };

      PDFService.generateTranscript(transcriptData);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate transcript PDF. Please try again.');
    }
  };

  // Speech synthesis function
  const speakMessage = async (text: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        // Ensure any ongoing speech is properly cancelled
        window.speechSynthesis.cancel();
        
        // Small delay to ensure previous speech is fully cancelled
        setTimeout(() => {
          try {
            // Create new utterance
            const utterance = new SpeechSynthesisUtterance(text);

            // Get voices and wait if necessary
            const getVoice = () => {
              const voices = window.speechSynthesis.getVoices();
              if (voices.length > 0) {
                const femaleVoice = voices.find(voice => 
                  voice.name.toLowerCase().includes('female') || 
                  voice.name.toLowerCase().includes('zira')
                ) || voices[0];
                utterance.voice = femaleVoice;
                return true;
              }
              return false;
            };

            // Try to get voice immediately
            if (!getVoice()) {
              // If no voices available, wait for them to load
              window.speechSynthesis.onvoiceschanged = () => {
                getVoice();
              };
            }

            // Set properties
            utterance.volume = gameSoundVolume / 100;
            utterance.rate = 0.9; // Slightly slower for better clarity
            utterance.pitch = 1.1; // Slightly higher pitch for female voice

            // Setup event handlers
            utterance.onstart = () => {
              setIsSpeaking(true);
            };

            utterance.onend = () => {
              setIsSpeaking(false);
              resolve();
            };

            utterance.onerror = (event) => {
              // Only log and reject if it's not an interruption
              if (event.error !== 'interrupted') {
                console.error('Speech synthesis error:', event);
                setIsSpeaking(false);
                reject(event);
              } else {
                // For interruptions, just resolve
                resolve();
              }
            };

            // Chrome fix: Keep the synthesis alive
            const keepAlive = setInterval(() => {
              if (window.speechSynthesis.speaking && !document.hidden) {
                window.speechSynthesis.pause();
                window.speechSynthesis.resume();
              } else {
                clearInterval(keepAlive);
              }
            }, 5000);

            // Speak the text
            window.speechSynthesis.speak(utterance);

            // Additional Chrome fix: Resume if paused
            if (window.speechSynthesis.paused) {
              window.speechSynthesis.resume();
            }

          } catch (error) {
            console.error('Speech synthesis setup error:', error);
            setIsSpeaking(false);
            reject(error);
          }
        }, 100); // Small delay before starting new speech

      } catch (error) {
        console.error('Speech synthesis outer error:', error);
        setIsSpeaking(false);
        reject(error);
      }
    });
  };

  // Add comprehensive audio stopping function
  const stopAllAudio = () => {
    // Stop background music
    stopBackgroundMusic();
    
    // Cancel any ongoing or queued speech synthesis
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    
    // Clear any pending timeouts or speech queues
    const highestId = Number(setTimeout(() => {}, 0));
    for (let i = highestId; i >= 0; i--) {
      clearTimeout(i);
    }
    
    // Force set audio state to inactive
    const audioElement = document.querySelector('audio');
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
  };

  // Handle return to menu
  const handleReturnToMenu = () => {
    // Stop all audio immediately
    stopAllAudio();
    
    // Small delay to ensure audio has stopped before navigation
    setTimeout(() => {
      navigate('/');
    }, 50);
  };

  // Handle successful exit button click
  const handleExitClick = () => {
    // Stop all audio immediately
    stopAllAudio();
    
    // Restore previous theme and side nav state
    document.documentElement.setAttribute('data-theme', previousTheme);
    document.body.classList.remove('sidebar-closed');
    document.body.classList.add('sidebar-open');
    
    // Small delay to ensure audio has stopped before navigation
    setTimeout(() => {
      navigate('/');
    }, 50);
  };

  // Add trust level calculation
  const calculateTrustLevel = (score: number): { level: string; color: string } => {
    if (score <= -10) return { level: 'Severed', color: 'var(--danger-color)' };
    if (score <= -5) return { level: 'Critical', color: 'var(--danger-color)' };
    if (score <= 0) return { level: 'Weakening', color: 'var(--warning-color)' };
    if (score <= 5) return { level: 'Stable', color: 'var(--text-color)' };
    if (score <= 10) return { level: 'Strong', color: 'var(--success-color)' };
    return { level: 'Unbreakable', color: 'var(--success-color)' };
  };

  // Modify handleAction to store previous visibility state when reaction occurs
  const handleAction = async (action: string) => {
    if (!gameData?.sessionId) {
      console.error('No valid session ID found');
      return;
    }

    try {
      // Reset any previous reaction
      setReactionType(undefined);
      
      setFailedAction(null);
      setMessages(prev => [...prev, { type: 'user', content: action }]);
      setIsThinking(true);
      setMessages(prev => [...prev, { type: 'system', content: '...', isThinking: true }]);
      
      const data: GameActionResponse = await performGameAction(gameData.sessionId, action);
      
      // Check for reaction field and handle stats visibility
      if (data.reaction || data.reactionType) {
        const reactionToUse = data.reaction || data.reactionType;
        setReactionType(reactionToUse);
        
        // Store current visibility state BEFORE changing it
        const wasVisible = isStatsVisible;
        setStatsVisible(true);
        
        // Reset the reaction and restore previous visibility after 8 seconds
        setTimeout(() => {
          setReactionType(undefined);
          setStatsVisible(wasVisible);
        }, 8000);
      }
      
      if (!data) {
        throw new Error('No response from game action');
      }
      
      setMessages(prev => {
        const filteredMessages = prev.filter(msg => !('isThinking' in msg));
        const newMessages = [...filteredMessages];
        
        if (data.feedback) {
          newMessages.push({ 
            type: 'system', 
            content: data.feedback,
            isFeedback: true 
          });
        }
        
        if (!data.game_over && !data.game_completed && data.next_prompt) {
          newMessages.push({ 
            type: 'system', 
            content: data.next_prompt 
          });
        }
        
        return newMessages;
      });

      // Set isThinking to false before starting speech synthesis
      setIsThinking(false);

      // Handle speech sequentially with proper error handling
      if (data.feedback || data.next_prompt) {
        try {
          if (data.feedback) {
            await speakMessage(data.feedback);
          }
          
          if (!data.game_over && !data.game_completed && data.next_prompt) {
            // Add a small pause between messages
            await new Promise(resolve => setTimeout(resolve, 500));
            await speakMessage(data.next_prompt);
          }
        } catch (error) {
          console.error('Error in speech sequence:', error);
          // Continue with the game even if speech fails
        }
      }
      
      setGameData(prev => ({
        ...prev!,
        status: data.status || prev!.status
      }));
      
      if (typeof data.score_adjustment === 'number') {
        const newScore = totalScore + data.score_adjustment;
        setTotalScore(newScore);
        
        if (newScore <= -10) {
          setIsGameOver(true);
          setGameOverMessage({
            title: 'The Spiritual Bond is Severed',
            message: 'Your connection to your descendant has been irreparably broken. They can no longer hear your guidance.'
          });
        }
      }
      
      // Check for both game_over and game_completed flags
      if (data.game_over || data.game_completed) {
        setIsGameOver(true);
        setGameOverMessage({
          title: 'The Spiritual Bond is Broken',
          message: data.feedback || 'Your journey has come to an end.'
        });
      }
      
      if (!data.game_over && !data.game_completed && Array.isArray(data.suggestions)) {
        setSuggestions(data.suggestions.slice(0, 3));
      }
    } catch (error) {
      console.error('Error performing action:', error);
      setFailedAction(action);
      setMessages(prev => {
        const filteredMessages = prev.filter(msg => !('isThinking' in msg));
        return [...filteredMessages, { 
          type: 'system', 
          content: 'Sorry, there was an error processing your action.',
          error: true
        }];
      });
      // Set isThinking to false in case of error
      setIsThinking(false);
    }
  };

  // Modify handleSubmit to include the guidance format
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userInput.trim()) return;
    handleAction(userInput.trim());
    setUserInput('');
  };

  // Add effect to set background image
  useEffect(() => {
    if (gameData?.background_image_url) {
      const container = document.querySelector('#root > .container');
      if (container) {
        (container as HTMLElement).style.backgroundImage = `url(${gameData.background_image_url})`;
      }
    }

    // Cleanup function to remove background image when component unmounts
    return () => {
      const container = document.querySelector('#root > .container');
      if (container) {
        (container as HTMLElement).style.backgroundImage = 'none';
      }
    };
  }, [gameData?.background_image_url]);

  // Set up gameplay environment
  useEffect(() => {
    // Store previous states
    setPreviousTheme(document.documentElement.getAttribute('data-theme') || 'light');
    
    // Set dark mode and minimize side nav
    document.documentElement.setAttribute('data-theme', 'dark');
    document.body.classList.remove('sidebar-open');
    document.body.classList.add('sidebar-closed');
    
    // Cleanup function to restore previous states
    return () => {
      document.documentElement.setAttribute('data-theme', previousTheme);
      document.body.classList.remove('sidebar-closed');
      document.body.classList.add('sidebar-open');
    };
  }, []);

  // Update the game over effect to also check score
  useEffect(() => {
    // Check for game over in messages
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'system' && 
          typeof lastMessage.content === 'object') {
        const content = lastMessage.content as unknown as MessageContent;
        if (content.game_over) {
          setIsGameOver(true);
          setGameOverMessage({
            title: 'The Spiritual Bond is Broken',
            message: content.feedback || 'Your journey has come to an end.'
          });
        }
      }
    }
    
    // Check for score-based game over
    if (totalScore <= -10) {
      setIsGameOver(true);
      setGameOverMessage({
        title: 'The Spiritual Bond is Severed',
        message: 'Your connection to your descendant has been irreparably broken. They can no longer hear your guidance.'
      });
    }
  }, [messages, totalScore]);

  const toggleStats = () => {
    setStatsVisible(!isStatsVisible);
  };

  useEffect(() => {
    if (isReactionReady) {
      setStatsVisible(true);
      const timer = setTimeout(() => {
        setStatsVisible(false);
      }, 3000); // Auto-close after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [isReactionReady]);

  // Render loading screen while game data is being fetched
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading your spiritual connection...</p>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="gameplay-container">
        <div className="error-screen">
          <h2>Error Loading Game</h2>
          <p>Unable to load game data. Please return to the home page and try again.</p>
          <button onClick={() => navigate('/')}>Return Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gameplay-container">
      {/* Game Over Modal */}
      {isGameOver && gameOverMessage && (
        <GameOverModal 
          gameOverMessage={gameOverMessage}
          onGenerateTranscript={generateTranscriptPDF}
          onReturnToMenu={handleReturnToMenu}
        />
      )}

      <div className="game-screen">
        <div className="chat-area">
          {/* Game Stats Component */}
          <button onClick={toggleStats} className={`floating-icon ${isStatsVisible ? 'hidden' : ''}`}>
            <FaMobileAlt />
          </button>
          {isStatsVisible && <GameStatus 
            statsPosition={statsPosition}
            isDraggingStats={isDraggingStats}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            totalScore={totalScore}
            gameType={gameData.gameType}
            difficulty={gameData.difficulty}
            status={gameData.status}
            isSpeaking={isSpeaking}
            bgMusicVolume={bgMusicVolume}
            gameSoundVolume={gameSoundVolume}
            setBgMusicVolume={setBgMusicVolume}
            setGameSoundVolume={setGameSoundVolume}
            onExitClick={handleExitClick}
            gameId={gameId}
            reactionType={reactionType}
            onClose={toggleStats}
          />}

          {/* Chat Container */}
          <div className="chat-card-wrapper">
            <div className="chat-card-background"></div>
            <div className="chat-card">
              {/* Chat Window Component */}
              <ChatWindow 
                messages={messages}
                failedAction={failedAction}
                isGameOver={isGameOver}
                isThinking={isThinking}
                onRetry={handleAction}
              />

              {/* Action Input Component */}
              {!isGameOver && (
                <ActionInput 
                  userInput={userInput}
                  setUserInput={setUserInput}
                  suggestions={suggestions}
                  isThinking={isThinking}
                  onSubmit={handleSubmit}
                  onSuggestionClick={handleAction}
                  guidanceFormats={guidanceFormats}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gameplay; 